import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
const STORAGE_KEY = 'ninja_coach_ready_countdown_target';
const ONE_DAY = 24 * 60 * 60 * 1000;
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + ONE_DAY;
  const storedTarget = window.localStorage.getItem(STORAGE_KEY);
  if (storedTarget) {
    const parsedTarget = Number.parseInt(storedTarget, 10);
    if (!Number.isNaN(parsedTarget) && parsedTarget > Date.now()) return parsedTarget;
  }
  const target = Date.now() + ONE_DAY;
  try {
    window.localStorage.setItem(STORAGE_KEY, target.toString());
  } catch {
    /* storage unavailable */
  }
  return target;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(ONE_DAY);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const difference = target - Date.now();
      if (difference <= 0) {
        const nextTarget = Date.now() + ONE_DAY;
        try {
          window.localStorage.setItem(STORAGE_KEY, nextTarget.toString());
        } catch {
          /* storage unavailable */
        }
        setTarget(nextTarget);
        setRemaining(ONE_DAY);
        return;
      }
      setRemaining(difference);
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = ONE_DAY - remaining;
  const progress = Math.min(90, 65 + elapsed / ONE_DAY * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="mt-10 max-w-3xl mx-auto bg-white border border-brand/30 rounded-2xl p-6 md:p-8 shadow-[0_10px_30px_-18px_rgba(27,26,25,0.25)]" data-id="element-42" data-mp="2igr0p3f05cku">
      <div className="text-center" data-id="element-43" data-mp="2jaq94mevbun9">
        <div className="flex items-center justify-center gap-2 text-brand-dark font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-44" data-mp="2je5mnksw8vmk">
          <AlarmClock size={20} data-id="element-45" data-mp="29e52jwn7abhb" />
          <span data-id="element-46" data-mp="29fg9g5bxzzcn">Launch pricing ends in:</span>
        </div>
        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-47" data-mp="24mn4phgh4r0k">
          <TimeBox value={pad(hours)} label="hrs" data-id="element-48" data-mp="1zo22xdlkpza0" />
          <span className="text-3xl md:text-5xl font-bold text-brand" data-id="element-49" data-mp="1zoliinnr4bkv">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-50" data-mp="1vni485lcqnlr" />
          <span className="text-3xl md:text-5xl font-bold text-brand" data-id="element-51" data-mp="1vknk6f6934sv">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-52" data-mp="1qmlzxqx38zft" />
        </div>
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-graphite mb-8" data-id="element-53" data-mp="1lpc2pp3gjjhf">
          After this, the price increases and won&apos;t come down again.
        </p>
        <div className="space-y-2" data-id="element-54" data-mp="64tl7i8estx4">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-55" data-mp="63s0sn5i6v2q">
            <span className="text-graphite" data-id="element-56" data-mp="b57jw9o4erih">Launch pricing spots remaining</span>
            <span className="text-brand-dark" data-id="element-57" data-mp="f4q8bff5hkq2">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-cream rounded-full overflow-hidden border border-silver-light" data-id="element-58" data-mp="3em7nax72udzw">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} className="h-full bg-brand rounded-full" data-id="element-59" data-mp="3jlaqsd7esc5r" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-graphite-light font-medium" data-id="element-60" data-mp="2vtvnah3wbosx">
            {Math.round(progress)}% of spots filled
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-cream-light border-2 border-brand/25 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px]" data-id="element-61" data-mp="2btbynz127at7">
      <div className="flex items-baseline justify-center gap-1" data-id="element-62" data-mp="2qj00d1d5kbhg">
        <span className="text-4xl md:text-5xl font-bold text-ink tabular-nums" data-id="element-63" data-mp="2lqgi3r3iss6b">{value}</span>
        <span className="text-xs md:text-sm font-medium text-brand-dark" data-id="element-64" data-mp="2zhbx7l4m45ic">{label}</span>
      </div>
    </div>;
}